import axios, { AxiosError } from "axios";
import { API_BASE_URL } from "@/config/endpoints";
import { GENERIC_SERVER_ERROR_MESSAGE } from "@/constants/errors";

let tokenProvider: (() => Promise<string | null>) | null = null;

export function setTokenProvider(fn: () => Promise<string | null>) {
  tokenProvider = fn;
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use(async (config) => {
  if (tokenProvider) {
    const token = await tokenProvider();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  // A FormData body must NOT carry the instance's JSON content type.
  //
  // multipart/form-data is only parseable with the boundary string that separates its parts, and
  // that boundary is generated when the body is serialised — so the header has to be set by the
  // browser, from the body, at send time. The default above pre-empts that: the request goes out
  // labelled application/json with a multipart body inside, and the server rejects it with
  // "Expected multipart/form-data" while the network tab shows a perfectly good file attached.
  //
  // Deleting the header here rather than at each call site, because every future upload would hit
  // this and the failure names neither the header nor the client.
  if (config.data instanceof FormData) {
    config.headers.delete("Content-Type");
  }

  return config;
});

// A 500 never shows the server's words to the user.
//
// GFG-427: a Drizzle "Failed query: insert into ..." message, with table and column names, reached
// a customer on screen. Every UI wrapper and `apiErrorMessage` relays `response.data.error`, so
// replacing the body here, once, covers all of them — no call site has to remember to scrub.
//
// The rule: 500 is always rewritten; 501-599 is rewritten only when the body is not a plain JSON
// object. During a deploy or crash the hosting proxy answers 502/503/504 with an HTML or text page,
// which would otherwise be shown raw. Our own 502/503/504 are JSON objects with curated copy
// ("The PDF service refused this request. Contact support if this persists.") that the
// impact-report UI reads, so those pass through. 4xx are the server's own sentence about what the
// user can fix, and network errors have no response to rewrite.
//
// The rejection stays an AxiosError and keeps its own status (500, or the 501-599 it came with) so
// status checks keep working. It carries a new response object rather than a mutated one, since the
// original may be shared with the adapter.
//
// A Blob body (the impact-report PDF download uses responseType "blob") counts as an object and is
// left to its caller, whose decodeBlobErrorBody drops non-JSON.
function isObjectBody(value: unknown): boolean {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function shouldScrub(status: number, data: unknown): boolean {
  if (status === 500) return true;
  return status > 500 && status <= 599 && !isObjectBody(data);
}

apiClient.interceptors.response.use(undefined, (error: unknown) => {
  if (!axios.isAxiosError(error) || !error.response) {
    return Promise.reject(error);
  }
  if (!shouldScrub(error.response.status, error.response.data)) {
    return Promise.reject(error);
  }

  return Promise.reject(
    AxiosError.from(error, error.code, error.config, error.request, {
      ...error.response,
      data: { success: false, error: GENERIC_SERVER_ERROR_MESSAGE },
    }),
  );
});
